import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Car, Clock, Calendar, ExternalLink } from "lucide-react";
import { BOOKING_URL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const STEPS = [
  { n: "01", icon: Car, t: "Pick your package", d: "Basic, interior, exterior or the full refresh — book it in under two minutes." },
  { n: "02", icon: MapPin, t: "We come to you", d: "Home, office, wherever your car sits. We bring everything we need." },
  { n: "03", icon: Clock, t: "Drive happy", d: "Dealership-level results without leaving your spot. That's the whole point." },
];

export const BookNow = () => {
  const [open, setOpen] = useState(false);
  const toggle = () => {
    if (!open) axios.post(`${API}/track/scheduler-open`).catch(() => {});
    setOpen((v) => !v);
  };
  return (
  <section
    id="book"
    data-testid="book-now-section"
    className="relative overflow-hidden border-y border-white/10 bg-[#0A0A0A] py-24 md:py-32"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#E60000]/10 blur-[120px]"
    />
    <div className="relative mx-auto max-w-5xl px-5 text-center md:px-10">
      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mb-5 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
      >
        Booking powered by Square — secure & instant
      </motion.p>

      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight sm:text-6xl md:text-7xl"
      >
        Ready when
        <br />
        <span className="text-outline-red">you are.</span>
      </motion.h2>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mt-12"
      >
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          data-testid="book-now-cta-button"
          className="group inline-flex items-center gap-3 bg-[#E60000] px-12 py-5 font-display text-lg font-semibold uppercase tracking-[0.2em] text-white shadow-[0_0_0_rgba(230,0,0,0)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] hover:shadow-[0_16px_50px_rgba(230,0,0,0.4)]"
        >
          {open ? "Hide Scheduler" : "Book Now"}
          {open ? (
            <Calendar size={22} strokeWidth={2.5} />
          ) : (
            <ArrowUpRight
              size={22}
              strokeWidth={2.5}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          )}
        </button>
      </motion.div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="square-scheduler"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 overflow-hidden"
            data-testid="square-scheduler-wrap"
          >
            <p
              data-testid="booking-nudge"
              className="mb-5 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400"
            >
              <MapPin size={14} className="text-[#E60000]" strokeWidth={2} />
              Serving Melbourne, Palm Bay &amp; Satellite Beach — pick a time
            </p>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white">
              <iframe
                src={BOOKING_URL}
                title="Book your detail with MARR Mobile Detailing"
                data-testid="square-scheduler-iframe"
                loading="lazy"
                className="h-[720px] w-full border-0"
                allow="payment"
              />
            </div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="square-open-newtab-link"
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-white"
            >
              Trouble booking? Open in a new tab
              <ExternalLink size={14} strokeWidth={2} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-16 grid grid-cols-1 gap-px border border-white/10 bg-white/10 text-left sm:grid-cols-3">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: i * 0.08 }}
            className="bg-[#0A0A0A] p-7"
            data-testid={`booking-step-${s.n}`}
          >
            <div className="flex items-center justify-between">
              <s.icon size={22} className="text-[#E60000]" strokeWidth={1.75} />
              <span className="font-display text-sm tracking-[0.3em] text-zinc-600">{s.n}</span>
            </div>
            <div className="font-display mt-5 text-lg font-semibold uppercase tracking-wide text-white">
              {s.t}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};

export default BookNow;
